import { PropTypes } from 'prop-types';
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useIntl } from 'react-intl';
import AccionaTableBody from './AccionaTableBody';
import AccionaTableHeader from './AccionaTableHeader';
import { useSelector } from 'react-redux';
import AccionaTablePagination from './AccionaTablePagination';
import { MainContext } from '../../context/MainContext';
import { useOutletContext } from 'react-router-dom';
import AccionaTableButton from './AccionaTableButton';

import styles from './index.module.scss';

const { clearFilters, header } = styles;

const AccionaTable = ({
  columns,
  getItems,
  entity,
  pageSize,
  setSelectedRows,
  selectedRows,
  showTitle,
  dataParseFunction,
  onChange,
  actions,
  initialFilters,
  allowClear,
  onClearFilters,
  allowClearStyle,
  expandable,
  isSelectionDisabled,
  title,
  rowHeight,
}) => {
  const intl = useIntl();
  const {
    list: items,
    total,
    fetching,
  } = useSelector((store) => store[entity]);
  const { setLoading } = useContext(MainContext);
  const { project } = useOutletContext() || {};
  const [pagination, setPagination] = useState({
    current: 1,
    total,
    pageSize,
  });
  const [filters, setFilters] = useState(
    initialFilters || {
      sortable: columns.docket_number?.ascSort ? 'docket_number-ASC' : '',
    },
  );
  const pid = project?.id_project;
  const { current: currentPage, pageSize: currentPageSize } = pagination;
  const prevPage = useRef();

  const onClickCheckBoxHeader = (checked) => {
    if (checked) {
      const allSelected = [];
      items.forEach((element) => {
        if (!isSelectionDisabled(element))
          allSelected.push(
            Object.assign(element, { page: pagination.current }),
          );
      });
      setSelectedRows(allSelected);
    } else {
      setSelectedRows([]);
    }
  };

  const onClickCheckBox = (checked, object) => {
    if (checked) {
      if (selectedRows.findIndex((x) => x.id === object.id) < 0) {
        setSelectedRows([
          ...selectedRows,
          Object.assign(object, { page: pagination.current }),
        ]);
      }
    } else {
      const index = selectedRows.findIndex((item) => item.id === object.id);
      setSelectedRows([
        ...selectedRows.slice(0, index),
        ...selectedRows.slice(index + 1),
      ]);
    }
  };

  const thereAreSomeFilterSelected = () => {
    const selectedFilters = Object.keys(filters).filter(
      (f) => f !== 'sortable',
    );
    return Boolean(selectedFilters.length);
  };

  useEffect(() => {
    getItems({
      size: currentPageSize,
      page: currentPage,
      filters: { pid, ...filters },
    });
    prevPage.current = currentPage;
  }, [currentPage, currentPageSize, filters, getItems, pid]);

  useEffect(() => {
    if (pagination.total !== total) {
      setPagination((prev) => ({ ...prev, total }));
    }
    if (setSelectedRows) setSelectedRows([]);
  }, [total, pagination, setSelectedRows]);

  useEffect(() => {
    const lastPage = Math.round(total / currentPageSize);
    if (
      items.length < currentPageSize &&
      total &&
      items.length < total &&
      prevPage.current !== lastPage
    ) {
      getItems({
        size: currentPageSize,
        page: prevPage.current,
        filters: { pid, ...filters },
      });
    }
  }, [currentPageSize, filters, getItems, items.length, pid, total]);

  useEffect(() => {
    setLoading(fetching);
  }, [fetching, setLoading]);

  useEffect(() => {
    onChange({ pagination, filters });
  }, [pagination, filters, onChange]);

  useEffect(() => {
    if (initialFilters) {
      setFilters(initialFilters);
    }
  }, [initialFilters]);

  return (
    <div className="row" id="AccionaTable">
      <div className={`col-sm-12 ${header}`}>
        {showTitle && (title || intl.formatMessage({ id: entity }))}
        {allowClear && thereAreSomeFilterSelected() && (
          <AccionaTableButton
            toolTip={intl.formatMessage({
              id: 'AccionaTable.resetFilters.button',
            })}
            type="filter"
            className={clearFilters}
            style={allowClearStyle}
            onClick={() => {
              setFilters({ sortable: 'docket_number:ASC' });
              onClearFilters();
            }}
          >
            <i className="fa fa-times fa-xs" />
          </AccionaTableButton>
        )}
      </div>
      <div className="col-sm-12">
        <table className="dataTable" style={{ backgroundColor: '#fff' }}>
          <AccionaTableHeader
            isSelectionDisabled={isSelectionDisabled}
            columns={columns}
            hiddenActionCol={!actions}
            hiddenCheckBox={setSelectedRows ? false : true}
            onClickCheckBoxHeader={onClickCheckBoxHeader}
            selectedRows={selectedRows}
            totalItems={items.length || 0}
            selectedPage={pagination.current}
            entity={entity}
            setFilters={setFilters}
            setPagination={setPagination}
            total={total}
            filters={filters}
            expandable={expandable}
            items={items}
          />
          {items.length === 0 ? (
            <tbody>
              <tr>
                <td
                  colSpan={
                    Object.keys(columns).length + (setSelectedRows ? 2 : 1)
                  }
                  className="no-results-msg"
                >
                  {intl.formatMessage({ id: 'table.noResults' })}
                </td>
              </tr>
            </tbody>
          ) : (
            <AccionaTableBody
              bodyData={dataParseFunction ? dataParseFunction(items) : items}
              columns={columns}
              onClickCheckBox={setSelectedRows ? onClickCheckBox : undefined}
              selectedRows={selectedRows}
              actions={actions}
              entity={entity}
              pageSize={pageSize}
              expandable={expandable}
              isSelectionDisabled={isSelectionDisabled}
              rowHeight={rowHeight}
            />
          )}
        </table>
        <div
          className="col-sm-12"
          style={{
            backgroundColor: '#fff',
            height: '43px',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <AccionaTablePagination
            pagination={pagination}
            setPagination={setPagination}
          />
        </div>
      </div>
    </div>
  );
};

AccionaTable.propTypes = {
  columns: PropTypes.object.isRequired,
  getItems: PropTypes.func.isRequired,
  entity: PropTypes.string.isRequired,
  pageSize: PropTypes.number,
  setSelectedRows: PropTypes.func,
  selectedRows: PropTypes.arrayOf(PropTypes.object),
  showTitle: PropTypes.bool,
  dataParseFunction: PropTypes.func,
  onChange: PropTypes.func,
  actions: PropTypes.func,
  initialFilters: PropTypes.object,
  allowClear: PropTypes.bool,
  onClearFilters: PropTypes.func,
  allowClearStyle: PropTypes.object,
  expandable: PropTypes.shape({
    rowExpandable: PropTypes.func,
    expandedRowRender: PropTypes.func,
  }),
  isSelectionDisabled: PropTypes.func,
  title: PropTypes.string,
  rowHeight: PropTypes.string,
};
AccionaTable.defaultProps = {
  pageSize: 5,
  selectedRows: [],
  showTitle: true,
  onChange: () => {},
  columns: {},
  allowClear: false,
  onClearFilters: () => {},
  allowClearStyle: {},
  isSelectionDisabled: () => {},
  dataParseFunction: undefined,
  actions: undefined,
  initialFilters: undefined,
  expandable: undefined,
  title: undefined,
  rowHeight: '94px',
  setSelectedRows: undefined,
};

export default AccionaTable;
